/**
 * Capital-return curve: cumulative Contribution Margin per customer climbs over
 * acquisition and overhead. Inventory and payout are separate time stages around
 * that curve. Product cost is already inside CM, so inventory must never become a
 * second dollar hurdle here.
 */

import { cashReturnDays, interpolatedCrossingDay, pctToFullBreakEven } from "@data-dashboard/shared";
import { inventoryEquationDays, inventoryTimingLabel } from "../lib/cash-timing-format.js";

export interface PaybackPoint { day: number; cumCm: number; cohortSize: number }
export interface PaybackData {
  points: PaybackPoint[];
  layers: { acquisition: number; overhead: number };
  hasCm: boolean;
  /** Whose margin the climbing line averages vs which window's costs
   *  (server-disclosed basis, e.g. "customers acquired in the last 12 months,
   *  vs that year's costs"). */
  cohortNote?: string;
  /** Server verdict: when set, the cost bar is unmeasurable in this window and
   *  the page renders the honest state instead of this chart. */
  unmeasurableReason?: "needs_full_month" | "before_spend_coverage" | null;
}

/** The full customer-payback bar. Inventory affects time before the sale and is
 *  not another dollar cost because product cost is already removed inside CM. */
export function paybackCostBar(data: Pick<PaybackData, "layers">): number {
  return data.layers.acquisition + data.layers.overhead;
}

/** How much of acquisition plus overhead the latest measured CM has recovered. */
export function paybackCostPct(data: PaybackData): number | null {
  if (!data.hasCm || data.points.length < 2) return null;
  const last = data.points[data.points.length - 1]!.cumCm;
  return pctToFullBreakEven(last, paybackCostBar(data));
}

export interface PaybackLayerDays {
  acquisition: number | null;
  overhead: number | null;
}

/** Linear-interpolated customer days for the two separate recovery stages,
 *  rounded to the same whole-day rule used by the server headline. */
export function paybackLayerDays(data: Pick<PaybackData, "points" | "layers">): PaybackLayerDays {
  const curve = data.points.map((point) => ({ day: point.day, cumulativeValue: point.cumCm }));

  const acquisition = interpolatedCrossingDay(curve, data.layers.acquisition);
  const overhead = interpolatedCrossingDay(curve, paybackCostBar(data));
  return { acquisition, overhead };
}

const money = (n: number): string =>
  (n < 0 ? "-" : "") + new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.abs(n));
const compactCount = (n: number): string => n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k` : n.toLocaleString();

interface LayerMark { name: string; color: string; amount: number; cumThreshold: number; day: number | null }

export function PaybackCurveChart({
  data,
  inventoryDays,
  settlementDays,
}: {
  data: PaybackData;
  inventoryDays: number | null;
  settlementDays: number | null;
}): React.ReactElement {
  if (!data.hasCm) {
    return <div style={notice}>Add your product costs to see this. Payback is built from Contribution Margin, so there is nothing to measure until costs are entered.</div>;
  }
  if (data.points.length < 2) {
    return <div style={notice}>Not enough customer history has matured yet. Widen the timeline to see the payback curve take shape.</div>;
  }

  const { acquisition, overhead } = data.layers;
  const covered = paybackLayerDays(data);
  const marks: LayerMark[] = [
    { name: "Acquisition", color: "#D85A30", amount: acquisition, cumThreshold: acquisition, day: covered.acquisition },
    { name: "Overhead", color: "#7F77DD", amount: overhead, cumThreshold: acquisition + overhead, day: covered.overhead },
  ];
  const total = paybackCostBar(data);
  const ownInventoryDays = inventoryDays;
  const payoutDays = Math.max(0, settlementDays ?? 0);
  const acquisitionStageDays = covered.acquisition;
  const overheadStageDays = covered.overhead !== null && covered.acquisition !== null
    ? Math.max(0, covered.overhead - covered.acquisition)
    : null;
  const totalCashDays = cashReturnDays({
    inventoryDays: ownInventoryDays,
    paybackDays: covered.overhead,
    settlementDays: payoutDays,
  });

  const pts = data.points;
  const n = pts.length;
  const W = 760, H = 360;
  const PAD = { l: 50, r: 116, t: 22, b: 50 };
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;

  const cums = pts.map((p) => p.cumCm);
  const rawYMax = Math.max(total, ...cums) * 1.08;
  const yMin = Math.min(0, ...cums);
  const yMax = rawYMax > yMin ? rawYMax : yMin + 1;
  const xi = (i: number): number => PAD.l + (n > 1 ? (i / (n - 1)) * iw : 0);
  // Even-spaced points; map an arbitrary day to its interpolated slot position.
  const dayToX = (day: number): number => {
    if (day <= pts[0]!.day) return xi(0);
    for (let i = 0; i < n - 1; i++) {
      if (day >= pts[i]!.day && day <= pts[i + 1]!.day) {
        const f = (day - pts[i]!.day) / (pts[i + 1]!.day - pts[i]!.day || 1);
        return xi(i + f);
      }
    }
    return xi(n - 1);
  };
  const y = (v: number): number => PAD.t + (ih * (yMax - v)) / (yMax - yMin);

  const linePts = pts.map((p, i) => `${xi(i)},${y(p.cumCm)}`).join(" ");
  const endCum = pts[n - 1]!.cumCm;

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }} role="img"
        aria-label="Cumulative contribution margin per customer crossing acquisition and overhead">
        <rect x={PAD.l} y={PAD.t} width={iw} height={Math.max(0, y(total) - PAD.t)} fill="rgba(29,158,117,0.10)" />

        {/* cost-layer thresholds (cumulative) + right-edge labels */}
        {marks.map((m) => (
          <g key={m.name}>
            <line x1={PAD.l} y1={y(m.cumThreshold)} x2={W - PAD.r} y2={y(m.cumThreshold)} stroke={m.color} strokeWidth={1} strokeDasharray="4 4" opacity={0.5} />
            <rect x={W - PAD.r + 8} y={y(m.cumThreshold) - 7} width={9} height={9} rx={2} fill={m.color} />
            <text x={W - PAD.r + 21} y={y(m.cumThreshold) + 1} fontSize={11.5} fontWeight={600} fill="var(--body)" className="dd-num">{m.name}</text>
            <text x={W - PAD.r + 21} y={y(m.cumThreshold) + 13} fontSize={10} fill="var(--faint)" className="dd-num">{money(m.cumThreshold)}</text>
          </g>
        ))}

        {/* vertical "covered" markers where the line crosses each layer (no text — see chips below) */}
        {marks.map((m) => m.day === null ? null : (
          <g key={`mk-${m.name}`}>
            <line x1={dayToX(m.day)} y1={y(m.cumThreshold)} x2={dayToX(m.day)} y2={H - PAD.b} stroke={m.color} strokeWidth={1.2} strokeDasharray="3 3" opacity={0.65} />
            <circle cx={dayToX(m.day)} cy={y(m.cumThreshold)} r={4.5} fill={m.color} />
          </g>
        ))}

        {/* the cumulative-CM line + each circle labelled with cumulative CM */}
        <polyline points={linePts} fill="none" stroke="var(--accent)" strokeWidth={2.75} strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => (
          <g key={p.day}>
            <circle cx={xi(i)} cy={y(p.cumCm)} r={3.2} fill="var(--accent)" />
            {i < n - 1 && <text x={xi(i)} y={y(p.cumCm) - 8} textAnchor="middle" fontSize={9.5} fill="var(--body-faint)" className="dd-num">{money(p.cumCm)}</text>}
          </g>
        ))}
        <text x={W - PAD.r - 2} y={y(endCum) - 9} textAnchor="end" fontSize={11.5} fontWeight={700} fill={endCum >= total ? "var(--good)" : "var(--accent)"} className="dd-num">{money(endCum)}/cust</text>

        {/* x axis: day + measured customer count. Day 1 is suppressed because
            day 0 carries the same starting population right beside it. */}
        {pts.map((p, i) => (
          <g key={p.day}>
            <text x={xi(i)} y={H - 22} textAnchor="middle" fontSize={10} fill="var(--faint)" className="dd-num">{p.day === 1 ? "" : p.day === 0 ? "day 0" : p.day}</text>
            {p.day !== 1 && <text x={xi(i)} y={H - 8} textAnchor="middle" fontSize={8.5} fill="var(--text-faint)" className="dd-num">n {compactCount(p.cohortSize)}</text>}
          </g>
        ))}
        <text x={PAD.l - 6} y={y(0) + 4} textAnchor="end" fontSize={10} fill="var(--faint)" className="dd-num">$0</text>
      </svg>

      {/* Four separate cash stages. Inventory is time before the sale, while the
          line measures acquisition and overhead recovery after the sale. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 10 }}>
        <JourneyStage color="#BA7517" name="Inventory" detail={ownInventoryDays === null ? "needs setup" : inventoryTimingLabel(ownInventoryDays)} />
        <JourneyStage color="#D85A30" name="Acquisition" detail={acquisitionStageDays === null ? "not recovered yet" : `${Math.round(acquisitionStageDays)}d after sale`} />
        <JourneyStage color="#7F77DD" name="Overhead" detail={overheadStageDays === null ? "not recovered yet" : `${Math.round(overheadStageDays)} more days`} />
        <JourneyStage color="#4F8C82" name="Payout" detail={payoutDays < 0.5 ? "same day" : `${Math.round(payoutDays)}d`} />
      </div>
      {totalCashDays !== null && acquisitionStageDays !== null && overheadStageDays !== null && (
        <div style={{ fontSize: 12.5, color: "var(--body)", marginTop: 9, lineHeight: 1.5 }}>
          Full cash return: <span className="dd-num">{inventoryEquationDays(ownInventoryDays ?? 0)}</span> inventory + <span className="dd-num">{Math.round(acquisitionStageDays)}d</span> acquisition + <span className="dd-num">{Math.round(overheadStageDays)}d</span> overhead + <span className="dd-num">{Math.round(payoutDays)}d</span> payout = <b className="dd-num" style={{ color: "var(--num)" }}>{Math.round(totalCashDays)} days ({(totalCashDays / 30.4375).toFixed(1)} months)</b>.
        </div>
      )}
      <div style={{ fontSize: 12, color: "var(--faint)", marginTop: 8, lineHeight: 1.5 }}>
        Product costs are already removed inside Contribution Margin, so inventory is shown as time before the sale and is not charged again. The line averages {data.cohortNote ?? "customers from the selected acquisition group against that group's costs"}. Acquisition and overhead both come from those acquisition dates. Each dot uses customers who reached that day. The small n shows how many were measured.
      </div>
    </div>
  );
}

function JourneyStage({ color, name, detail }: { color: string; name: string; detail: string }): React.ReactElement {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--body)" }}>
      <span style={{ width: 9, height: 9, borderRadius: 2, background: color, display: "inline-block" }} />
      <b>{name}</b> <span className="dd-num" style={{ color: detail.includes("not ") || detail.includes("needs ") ? "var(--faint)" : undefined }}>{detail}</span>
    </span>
  );
}

const notice: React.CSSProperties = {
  padding: "22px 18px", background: "var(--surface-2)", border: "1px dashed var(--border)",
  borderRadius: "var(--r-md)", color: "var(--body)", fontSize: 13, lineHeight: 1.55, marginTop: 4,
};
